import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Empty, FilterGroup, PageHero, SortBar, toggleIn, useQuery } from '../../shared/components/Common';
import Icon from '../../shared/components/Icons';
import { formatPrice } from '../../shared/data/common';
import { useInsuranceSearch } from '../../api/insurance';
import {
  agesLabel, datesLabel, destinationLabel, durationLabel, journeyLabel, parseInsuranceQuery, searchProblem,
  toInsuranceParams, toSearchRequest,
} from './searchModel';
import { benefitValue, coverValue, groupBenefits, insurerName, keyBenefits, planTitle, regionTitle } from './planFormat';

const MAX_COMPARE = 3;
const planKey = (p) => `${p.supplierCode}|${p.planId}|${p.productId}`;

/** Link to a plan's details page; the search stays in the URL so the page survives a refresh. */
export function planPath(search, plan) {
  const params = new URLSearchParams({
    ...toInsuranceParams(search),
    supplier: plan.supplierCode,
    planId: plan.planId,
    productId: plan.productId,
  });
  return `/insurance/plan?${params.toString()}`;
}

function PlanCard({ plan, travellers, comparing, onCompare, compareFull, onOpen }) {
  const [expanded, setExpanded] = useState(false);
  const benefits = keyBenefits(plan);
  return (
    <article className="ins-card">
      <div className="ins-card-main">
        <span className="ins-shield"><Icon name="insurance" size={26} strokeWidth={1.6} /></span>
        <div className="ins-card-info">
          <span className="tag">{insurerName(plan.insurer)}</span>
          <h3>{planTitle(plan)}</h3>
          <span className="ins-cover">Medical cover up to <b>{plan.coverAmount || '—'}</b>{plan.regionName && <> · {regionTitle(plan.regionName)}</>}</span>
          <ul className="ins-benefits">
            {benefits.map((b) => (
              <li key={b.name}><Icon name="check" size={14} strokeWidth={2.5} /> <span>{b.name}</span>{b.sumInsured && <b>{b.sumInsured}</b>}</li>
            ))}
          </ul>
        </div>
        <div className="ins-card-price">
          <b className="price big">{formatPrice(plan.price.total)}</b>
          <span className="muted small">for {travellers} traveller{travellers === 1 ? '' : 's'}</span>
          {plan.price.convenienceFee > 0 && (
            <span className="muted small">{formatPrice(plan.price.premium)} premium + {formatPrice(plan.price.convenienceFee)} fee</span>
          )}
          <button type="button" className="btn btn-primary" onClick={onOpen}>View plan</button>
          <label className="check compare-check">
            <input type="checkbox" checked={comparing} disabled={!comparing && compareFull} onChange={(e) => onCompare(e.target.checked)} />
            <span>Compare</span>
          </label>
        </div>
      </div>
      <div className="ins-card-foot">
        <button type="button" className="link small" onClick={() => setExpanded(!expanded)} aria-expanded={expanded}>
          {expanded ? 'Hide benefits' : `All ${plan.benefits.length} benefits`}
          <Icon name="down" size={12} className={expanded ? 'flip-v' : ''} />
        </button>
        {plan.assistanceProviders.length > 0 && <span className="muted small">Assistance by {plan.assistanceProviders.join(', ')}</span>}
      </div>
      {expanded && (
        <div className="ins-benefit-groups">
          {groupBenefits(plan).map(([group, items]) => (
            <div key={group}>
              <h4>{group}</h4>
              <ul>
                {items.map((b) => (
                  <li key={b.name} title={b.description || undefined}>
                    <span>{b.name}</span><b>{benefitValue(b)}</b>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

function CompareTable({ plans, travellers, onRemove, onOpen }) {
  const names = [];
  plans.forEach((p) => p.benefits.forEach((b) => { if (!names.includes(b.name)) names.push(b.name); }));
  const value = (plan, name) => {
    const b = plan.benefits.find((x) => x.name === name);
    return b ? benefitValue(b) : null;
  };
  return (
    <section className="panel compare" id="insurance-compare">
      <h3>Compare plans</h3>
      <div className="table-scroll">
        <table className="data-table compare-table">
          <thead>
            <tr>
              <th>Benefit</th>
              {plans.map((p) => (
                <th key={planKey(p)}>
                  <b>{planTitle(p)}</b><span className="muted small">{p.coverAmount}</span>
                  <button type="button" className="link small" onClick={() => onRemove(p)}>Remove</button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="compare-price">
              <td>Total price ({travellers} traveller{travellers === 1 ? '' : 's'})</td>
              {plans.map((p) => <td key={planKey(p)}><b>{formatPrice(p.price.total)}</b></td>)}
            </tr>
            <tr><td>Insurer</td>{plans.map((p) => <td key={planKey(p)}>{insurerName(p.insurer)}</td>)}</tr>
            {names.map((name) => (
              <tr key={name}>
                <td>{name}</td>
                {plans.map((p) => {
                  const v = value(p, name);
                  return <td key={planKey(p)} className={v ? '' : 'muted'}>{v || '—'}</td>;
                })}
              </tr>
            ))}
            <tr>
              <td />
              {plans.map((p) => <td key={planKey(p)}><button type="button" className="btn btn-primary btn-sm" onClick={() => onOpen(p)}>View plan</button></td>)}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function InsuranceList() {
  const q = useQuery();
  const navigate = useNavigate();
  const search = parseInsuranceQuery(q);
  const problem = searchProblem(search);
  const { loading, data, error, retry } = useInsuranceSearch(problem ? null : toSearchRequest(search));
  const [sort, setSort] = useState('price');
  const [insurerFilter, setInsurerFilter] = useState([]);
  const [coverFilter, setCoverFilter] = useState([]);
  const [compare, setCompare] = useState([]);
  const [showCompare, setShowCompare] = useState(false);

  const plans = useMemo(() => data?.plans || [], [data]);
  const insurers = [...new Set(plans.map((p) => p.insurer).filter(Boolean))];
  const covers = [...new Set(plans.map((p) => p.coverAmount).filter(Boolean))].sort((a, b) => coverValue({ coverAmount: a }) - coverValue({ coverAmount: b }));
  const list = plans
    .filter((p) => !insurerFilter.length || insurerFilter.includes(p.insurer))
    .filter((p) => !coverFilter.length || coverFilter.includes(p.coverAmount))
    .sort((a, b) => (sort === 'cover' ? coverValue(b) - coverValue(a) || a.price.total - b.price.total : a.price.total - b.price.total));
  const compared = compare.map((key) => plans.find((p) => planKey(p) === key)).filter(Boolean);
  const travellers = search.ages.length;
  const open = (plan) => navigate(planPath(search, plan), { state: { plan } });

  const query = toInsuranceParams(search);
  const summary = {
    tag: journeyLabel(search.journey),
    items: [
      ['Travelling to', destinationLabel(search.dest)],
      ['Cover', `${datesLabel(search)} · ${durationLabel(search)}`],
      ['Travellers', agesLabel(search.ages)],
    ],
  };

  return (
    <>
      <PageHero tab="insurance" title="Compare travel insurance plans" query={query} summary={summary}
        subtitle={`${journeyLabel(search.journey)} · ${destinationLabel(search.dest)} · ${durationLabel(search)} · ${agesLabel(search.ages)}`} />
      <div className="container listing">
        <aside className="filters">
          <div className="filters-head"><b>Filters</b>
            <button type="button" className="link small" onClick={() => { setInsurerFilter([]); setCoverFilter([]); }}>Clear all</button>
          </div>
          <FilterGroup title="Medical cover">
            {covers.length ? covers.map((c) => (
              <Check key={c} label={`Up to ${c}`} checked={coverFilter.includes(c)} onChange={(on) => setCoverFilter(toggleIn(coverFilter, c, on))}
                extra={plans.filter((p) => p.coverAmount === c).length} />
            )) : <span className="muted small">—</span>}
          </FilterGroup>
          <FilterGroup title="Insurer">
            {insurers.length ? insurers.map((i) => (
              <Check key={i} label={insurerName(i)} checked={insurerFilter.includes(i)} onChange={(on) => setInsurerFilter(toggleIn(insurerFilter, i, on))} />
            )) : <span className="muted small">—</span>}
          </FilterGroup>
          <FilterGroup title="Good to know">
            <ul className="policy-list small-list">
              <li>Prices are for all travellers and include taxes.</li>
              <li>Cancel free of charge up to 24 hours before cover starts (convenience fee is non-refundable).</li>
              <li>Policies are emailed instantly and are valid for visa applications.</li>
            </ul>
          </FilterGroup>
        </aside>

        <div className="results">
          {problem && <div className="api-message error" role="alert">{problem} Use <b>Modify Search</b> above.</div>}
          {!problem && loading && (
            <div className="api-message" role="status"><span className="spinner sm" /> Finding the best plans for your trip…</div>
          )}
          {!problem && error && (
            <div className="api-message error" role="alert">
              <p>{error}</p>
              <button type="button" className="btn btn-outline btn-sm" onClick={retry}>Try again</button>
            </div>
          )}
          {data?.warnings?.map((w) => <div key={w} className="api-message warning">{w}</div>)}
          {data && (
            <>
              <SortBar count={list.length} label={`plan${list.length === 1 ? '' : 's'}`} sort={sort} setSort={setSort}
                options={[['price', 'Lowest price'], ['cover', 'Highest cover']]} />
              {list.length === 0 && (
                <Empty text={plans.length ? 'No plans match your filters.' : 'No plans are available for this trip. Try different dates, destinations or ages.'} />
              )}
              {list.map((plan) => {
                const key = planKey(plan);
                return (
                  <PlanCard key={key} plan={plan} travellers={travellers} onOpen={() => open(plan)}
                    comparing={compare.includes(key)} compareFull={compare.length >= MAX_COMPARE}
                    onCompare={(on) => setCompare(toggleIn(compare, key, on))} />
                );
              })}
              {showCompare && compared.length >= 2 && (
                <CompareTable plans={compared} travellers={travellers} onOpen={open}
                  onRemove={(p) => setCompare(compare.filter((k) => k !== planKey(p)))} />
              )}
            </>
          )}
        </div>
      </div>

      {compared.length > 0 && (
        <div className="compare-bar" role="region" aria-label="Plans to compare">
          <div className="container compare-bar-inner">
            <span><b>{compared.length}</b> of {MAX_COMPARE} plans selected: {compared.map(planTitle).join(', ')}</span>
            <div className="compare-bar-actions">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setCompare([]); setShowCompare(false); }}>Clear</button>
              <button type="button" className="btn btn-primary btn-sm" disabled={compared.length < 2}
                onClick={() => {
                  setShowCompare(true);
                  setTimeout(() => document.getElementById('insurance-compare')?.scrollIntoView?.({ behavior: 'smooth' }), 0);
                }}>
                {compared.length < 2 ? 'Select 2 to compare' : 'Compare now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
